import { angulosDaFoto, COR_PADRAO_DO_MARCADOR, PONTOS_DE_REFERENCIA, pontosNecessarios, type Ponto } from '@equiview/domain';
import { markersV1, traitsV1 } from '@equiview/protocol';
import { useMemo, useRef, useState, type KeyboardEvent, type MouseEvent, type PointerEvent } from 'react';
import { useParams } from 'react-router';
import { CorDoAdesivo } from '../components/CorDoAdesivo.js';
import { useUrlDaMidia } from '../components/FotoDaMidia.js';
import { SemCadastro } from '../components/SemCadastro.js';
import { Aguardar, Botao, BotaoLink, CabecalhoPagina, classeTd, classeTh, EstadoVazio, Painel, Seletor, Tabela } from '../components/ui/index.js';
import { formatarValor } from '../dados/catalogo.js';
import { useConsulta, useFonte, usePodeCadastrar } from '../dados/contexto.js';
import type { Animal, MedicaoPorFoto, Medida, Midia, OrigemDosPontos, PontosMarcados } from '../dados/fonte.js';
import { corNaFoto, detectarNaFoto } from '../lib/deteccao.js';

const ANGULOS = traitsV1.filter((t) => t.kind === 'angulo').sort((a, b) => a.sortOrder - b.sortOrder);
const ORDEM = pontosNecessarios(ANGULOS, markersV1.map((m) => m.code));
const MARCADORES = new Set(markersV1.map((m) => m.code));
/** Versao do detector gravada junto dos pontos: mede quanto o avaliador corrige. */
const DETECTOR = 'cor+esqueleto-v1';
const ROTULO: Record<string, string> = Object.fromEntries([
  ...markersV1.map((m) => [m.code, m.label]),
  ...PONTOS_DE_REFERENCIA.map((p) => [p.codigo, p.rotulo]),
]);

/**
 * Fotogrametria (Fase 1): marcar os pontos na foto de perfil e ler os 11
 * angulos. Feito no escritorio, com calma e zoom — e comparado depois com o
 * goniometro do campo, no relatorio de repetibilidade.
 *
 * "Detectar marcadores" acha os adesivos pela cor e diz qual e cada um; o
 * avaliador confere (os duvidosos vem destacados), arrasta o que precisar e
 * marca a mao a linha do solo e os cascos.
 */
export function Fotogrametria() {
  const { id = '', midiaId = '' } = useParams();
  const fonte = useFonte();
  const equipe = usePodeCadastrar();
  const consulta = useConsulta(
    async (f) => {
      const [animal, midia] = await Promise.all([f.animal(id), f.midia(midiaId)]);
      if (!animal || !midia || (!f.cadastro && !f.medicaoPorFoto)) return null;
      const lerMedicao = f.cadastro ? f.cadastro.medicaoPorFoto(midia.assessment_id) : f.medicaoPorFoto!(midia.assessment_id);
      const [medicao, todas] = await Promise.all([lerMedicao, f.medidas([midia.assessment_id])]);
      // "Campo" e o goniometro: um angulo que ja veio da foto nao serve de comparacao.
      const medidas = todas.filter((m) => m.origem !== 'foto');
      return { animal, midia, medicao, medidas };
    },
    [id, midiaId],
  );

  // Demonstracao: a tela abre para ver (e testar a deteccao), sem salvar nada.
  const soVer = !fonte.cadastro || !equipe;
  if (soVer && !fonte.medicaoPorFoto) return <SemCadastro demo={fonte.ficticia} />;

  return (
    <Aguardar consulta={consulta}>
      {(d) =>
        !d || d.midia.kind !== 'foto' ? (
          <EstadoVazio titulo="Foto não encontrada" acao={<BotaoLink to={`/animais/${id}`}>Voltar à ficha</BotaoLink>} />
        ) : (
          <Marcacao key={d.midia.id} {...d} soVer={soVer} />
        )
      }
    </Aguardar>
  );
}

function Marcacao({
  animal,
  midia,
  medicao,
  medidas,
  soVer,
}: {
  animal: Animal;
  midia: Midia;
  medicao: MedicaoPorFoto;
  medidas: Medida[];
  soVer: boolean;
}) {
  const cadastro = useFonte().cadastro;
  const url = useUrlDaMidia(midia);
  // Pontos de outra foto (remarcacao numa foto nova) nao servem para esta.
  const mesmaFoto = medicao.midiaId === midia.id;
  const [pontos, setPontos] = useState<PontosMarcados>(mesmaFoto ? medicao.pontos : {});
  const [origem, setOrigem] = useState<OrigemDosPontos>(mesmaFoto ? medicao.origem : {});
  const [conferir, setConferir] = useState<string[]>([]);
  const [ativo, setAtivo] = useState<string>(() => ORDEM.find((c) => !pontos[c]) ?? ORDEM[0]!);
  const [zoom, setZoom] = useState<'1' | '2' | '3'>('1');
  const [proporcao, setProporcao] = useState(1);
  const [cor, setCor] = useState(midia.marker_color ?? COR_PADRAO_DO_MARCADOR);
  const [lendoCor, setLendoCor] = useState(false);
  const [detectando, setDetectando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [mensagem, setMensagem] = useState<string | null>(null);
  const area = useRef<HTMLDivElement>(null);
  const arrasto = useRef<string | null>(null);

  const angulos = useMemo(() => angulosDaFoto(pontos, ANGULOS, proporcao), [pontos, proporcao]);
  const campo = (code: string) => medidas.find((m) => m.trait_code === code && m.side === 'NA')?.value_num ?? null;
  const marcados = ORDEM.filter((c) => pontos[c]).length;
  const usouDetector = Object.values(origem).includes('auto');

  /** Ponto posto ou ajustado pelo avaliador: vira manual e sai da conferencia. */
  const porAMao = (codigo: string, ponto: Ponto) => {
    setPontos((p) => ({ ...p, [codigo]: ponto }));
    setOrigem((o) => ({ ...o, [codigo]: 'manual' }));
    setConferir((c) => c.filter((x) => x !== codigo));
    setMensagem(null);
  };

  const naFoto = (e: { clientX: number; clientY: number }): Ponto => {
    const r = area.current!.getBoundingClientRect();
    const limitar = (v: number) => Math.min(1, Math.max(0, v));
    return { x: limitar((e.clientX - r.left) / r.width), y: limitar((e.clientY - r.top) / r.height) };
  };

  const tocar = (e: MouseEvent<HTMLDivElement>) => {
    const ponto = naFoto(e);
    if (lendoCor) {
      void lerCor(ponto);
      return;
    }
    porAMao(ativo, ponto);
    // Segue para o proximo a conferir ou que falta, na ordem.
    const novos = { ...pontos, [ativo]: ponto };
    const i = ORDEM.indexOf(ativo);
    const resto = [...ORDEM.slice(i + 1), ...ORDEM.slice(0, i)];
    const proximo = resto.find((c) => conferir.includes(c)) ?? resto.find((c) => !novos[c]);
    if (proximo) setAtivo(proximo);
  };

  const mudarCor = async (hex: string) => {
    setCor(hex);
    if (!soVer && cadastro) await cadastro.salvarDadosDaFoto(midia.id, { corDoMarcador: hex });
  };

  const lerCor = async (ponto: Ponto) => {
    setLendoCor(false);
    try {
      const hex = await corNaFoto(url!, ponto.x, ponto.y);
      await mudarCor(hex);
      setMensagem(`Cor do adesivo lida na foto: ${hex}. Agora toque em "Detectar marcadores".`);
    } catch (e) {
      setMensagem(e instanceof Error ? e.message : String(e));
    }
  };

  const detectar = async () => {
    if (!url) return;
    setDetectando(true);
    setMensagem(null);
    try {
      const r = await detectarNaFoto(url, cor);
      const achados = Object.keys(r.pontos).filter((c) => MARCADORES.has(c));
      if (achados.length === 0) {
        setMensagem(
          r.manchas === 0
            ? 'Nenhum adesivo dessa cor na foto. Confira a cor (ou use "Ler a cor na foto") e tente de novo.'
            : `Achei ${r.manchas} mancha(s) da cor, mas não deu para dizer quais marcadores são. Marque à mão.`,
        );
        return;
      }
      // So os marcadores mudam: linha do solo e cascos ficam como estavam.
      const semMarcadores = <T,>(o: Record<string, T>) => Object.fromEntries(Object.entries(o).filter(([c]) => !MARCADORES.has(c)));
      setPontos((p) => ({ ...semMarcadores(p), ...r.pontos }));
      setOrigem((o) => ({ ...semMarcadores(o), ...Object.fromEntries(achados.map((c) => [c, 'auto' as const])) }));
      setConferir(r.conferir);
      const faltam = ORDEM.filter((c) => MARCADORES.has(c) && !r.pontos[c]);
      setAtivo(r.conferir[0] ?? faltam[0] ?? ORDEM.find((c) => !MARCADORES.has(c) && !pontos[c]) ?? ativo);
      setMensagem(
        [
          `Identifiquei ${achados.length} marcador(es) em ${r.manchas} mancha(s).`,
          r.conferir.length ? `Confira os destacados: ${r.conferir.join(', ')}.` : '',
          faltam.length ? `Sem adesivo achado: ${faltam.join(', ')}.` : '',
          r.lado === 'direito' ? 'Atenção: o cavalo aparece com o lado DIREITO; o protocolo pede o esquerdo.' : '',
          'Depois marque a linha do solo e os cascos.',
        ]
          .filter(Boolean)
          .join(' '),
      );
    } catch (e) {
      setMensagem(e instanceof Error ? e.message : String(e));
    } finally {
      setDetectando(false);
    }
  };

  const salvar = async () => {
    if (soVer || !cadastro) return;
    setSalvando(true);
    setMensagem(null);
    try {
      await cadastro.salvarMedicaoPorFoto(midia.assessment_id, midia.id, pontos, angulos, {
        origem,
        detector: usouDetector ? DETECTOR : null,
      });
      setMensagem('Ângulos salvos. Eles sobem na próxima sincronização.');
    } catch (e) {
      setMensagem(e instanceof Error ? e.message : String(e));
    } finally {
      setSalvando(false);
    }
  };

  // Arrastar um ponto ja posto para ajusta-lo (dedo ou mouse).
  const pegar = (codigo: string) => (e: PointerEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    e.currentTarget.setPointerCapture(e.pointerId);
    arrasto.current = codigo;
    setAtivo(codigo);
  };
  const arrastar = (codigo: string) => (e: PointerEvent<HTMLButtonElement>) => {
    if (arrasto.current === codigo) porAMao(codigo, naFoto(e));
  };
  const soltar = () => {
    arrasto.current = null;
  };
  // Setas: ajuste fino, um pixel da tela por toque.
  const ajustar = (codigo: string) => (e: KeyboardEvent<HTMLButtonElement>) => {
    const passo = ({ ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] } as Record<string, [number, number]>)[e.key];
    const p = pontos[codigo];
    if (!passo || !p || !area.current) return;
    e.preventDefault();
    const r = area.current.getBoundingClientRect();
    porAMao(codigo, { x: p.x + passo[0] / r.width, y: p.y + passo[1] / r.height });
  };

  const estilo = (codigo: string) =>
    conferir.includes(codigo)
      ? 'bg-topo-aviso ring-2 ring-topo-aviso/60'
      : codigo === ativo
        ? 'bg-latao'
        : origem[codigo] === 'auto'
          ? 'bg-white ring-2 ring-destaque'
          : 'bg-destaque';

  return (
    <div className="space-y-6">
      <CabecalhoPagina
        trilha={[
          { rotulo: 'Plantel', para: '/animais' },
          { rotulo: animal.name, para: `/animais/${animal.id}` },
          { rotulo: 'Ângulos na foto' },
        ]}
        titulo="Ângulos na foto de perfil"
        meta={
          <span>
            {soVer
              ? 'Só para ver: dá para detectar os marcadores e arrastar os pontos, mas nada é salvo. '
              : 'Detecte os marcadores ou marque cada ponto tocando na foto. '}
            {marcados}/{ORDEM.length} marcados
            {medicao.autor ? ` · última medição por ${medicao.autor}` : ''}.
          </span>
        }
        acoes={
          <>
            <Botao carregando={detectando} disabled={!url || lendoCor} onClick={() => void detectar()}>
              Detectar marcadores
            </Botao>
            {!soVer && (
              <Botao variante="principal" carregando={salvando} disabled={marcados === 0} onClick={() => void salvar()}>
                Salvar ângulos
              </Botao>
            )}
          </>
        }
      />
      {mensagem && (
        <p role="status" className="border-l-2 border-destaque px-3 py-2 text-sm">
          {mensagem}
        </p>
      )}

      <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_20rem]">
        <Painel
          titulo={lendoCor ? 'Toque no centro de um adesivo' : `Ponto da vez: ${ativo} — ${ROTULO[ativo] ?? ''}`}
          corpo="p-0"
          acoes={
            <Seletor
              rotulo="Zoom"
              valor={zoom}
              onMudar={setZoom}
              opcoes={(['1', '2', '3'] as const).map((z) => ({ valor: z, conteudo: <span className="px-1 py-1">{z}×</span> }))}
            />
          }
        >
          {!url ? (
            <p className="p-4 text-sm text-apagado">
              O arquivo da foto não está neste aparelho nem acessível agora. Conecte-se à internet para abrir a foto.
            </p>
          ) : (
            <div className="max-h-[75vh] overflow-auto bg-abismo">
              <div
                ref={area}
                role="application"
                aria-label="Foto para marcar os pontos"
                className={`relative ${lendoCor ? 'cursor-copy' : 'cursor-crosshair'}`}
                style={{ width: `${Number(zoom) * 100}%` }}
                onClick={tocar}
              >
                <img
                  src={url}
                  alt="Perfil esquerdo"
                  draggable={false}
                  className="block w-full select-none"
                  onLoad={(e) => setProporcao(e.currentTarget.naturalWidth / e.currentTarget.naturalHeight || 1)}
                />
                {Object.entries(pontos).map(([codigo, p]) => (
                  <button
                    key={codigo}
                    type="button"
                    aria-label={`${codigo}: arraste ou use as setas para ajustar`}
                    className="absolute grid size-7 -translate-x-1/2 -translate-y-1/2 cursor-grab touch-none place-items-center active:cursor-grabbing"
                    style={{ left: `${p.x * 100}%`, top: `${p.y * 100}%` }}
                    onClick={(e) => e.stopPropagation()}
                    onPointerDown={pegar(codigo)}
                    onPointerMove={arrastar(codigo)}
                    onPointerUp={soltar}
                    onPointerCancel={soltar}
                    onKeyDown={ajustar(codigo)}
                  >
                    <span className={`block size-3 rounded-full border-2 border-white ${estilo(codigo)}`} />
                    <span className="pointer-events-none absolute top-5 left-4 bg-abismo/80 px-1 font-mono text-[10px] text-white">{codigo}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </Painel>

        <div className="space-y-6">
          <Painel titulo="Cor do adesivo">
            <div className="space-y-3">
              <CorDoAdesivo
                valor={cor}
                desabilitado={detectando}
                onMudar={(hex) => void mudarCor(hex).catch((e: unknown) => setMensagem(e instanceof Error ? e.message : String(e)))}
              />
              <Botao tamanho="sm" disabled={!url || detectando} onClick={() => setLendoCor((v) => !v)}>
                {lendoCor ? 'Cancelar leitura da cor' : 'Ler a cor na foto'}
              </Botao>
              <p className="text-xs text-apagado">
                Na foto: branco com anel = achado pela detecção; amarelo = conferir; azul = marcado à mão.
              </p>
            </div>
          </Painel>

          <Painel titulo="Pontos" corpo="p-0">
            <ul className="max-h-80 divide-y divide-borda overflow-y-auto text-sm">
              {ORDEM.map((codigo) => (
                <li key={codigo}>
                  <button
                    type="button"
                    onClick={() => setAtivo(codigo)}
                    className={`flex w-full items-center justify-between gap-2 px-3 py-2 text-left ${codigo === ativo ? 'bg-destaque-fundo' : ''}`}
                  >
                    <span>
                      <span className="font-mono font-semibold">{codigo}</span> <span className="text-apagado">{ROTULO[codigo]}</span>
                    </span>
                    <span className={`shrink-0 ${conferir.includes(codigo) ? 'text-aviso' : pontos[codigo] ? 'text-ok' : 'text-apagado'}`}>
                      {conferir.includes(codigo) ? 'conferir' : pontos[codigo] ? (origem[codigo] === 'auto' ? 'auto ✓' : '✓') : '—'}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
            {pontos[ativo] && (
              <div className="flex flex-wrap gap-2 border-t border-borda p-3">
                {conferir.includes(ativo) && (
                  <Botao tamanho="sm" onClick={() => setConferir((c) => c.filter((x) => x !== ativo))}>
                    {ativo} está certo
                  </Botao>
                )}
                <Botao
                  tamanho="sm"
                  variante="discreto"
                  onClick={() => {
                    const { [ativo]: _, ...resto } = pontos;
                    setPontos(resto);
                    setConferir((c) => c.filter((x) => x !== ativo));
                  }}
                >
                  Limpar {ativo}
                </Botao>
              </div>
            )}
          </Painel>

          <Painel titulo="Ângulos" corpo="p-0">
            <Tabela rotulo="Ângulos">
              <thead>
                <tr>
                  <th scope="col" className={classeTh}>Ângulo</th>
                  <th scope="col" className={`${classeTh} text-right`}>Foto</th>
                  <th scope="col" className={`${classeTh} text-right`}>Campo</th>
                </tr>
              </thead>
              <tbody>
                {ANGULOS.map((t) => {
                  const f = angulos[t.code];
                  const c = campo(t.code);
                  return (
                    <tr key={t.code}>
                      <td className={classeTd}>{t.label}</td>
                      <td className={`${classeTd} numero text-right`}>{f == null ? '—' : `${formatarValor(f, t)}°`}</td>
                      <td className={`${classeTd} numero text-right text-apagado`}>
                        {c == null ? '—' : `${formatarValor(c, t)}°`}
                        {f != null && c != null && <span className="block text-xs">{f - c > 0 ? '+' : ''}{formatarValor(f - c, t)}°</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Tabela>
            <p className="border-t border-borda px-3 py-2 text-xs text-apagado">
              As inclinações usam a linha do solo marcada (SOLO_1 e SOLO_2); sem ela, a horizontal da foto.
            </p>
          </Painel>
        </div>
      </div>
    </div>
  );
}
